import type { PublicHoliday } from '@techgitter/contracts';
import type { BusinessHoursConfig, TimeWindowResult } from '@techgitter/contracts';
import { BUSINESS_HOURS_DEFAULT } from '@techgitter/contracts';
import { getBusinessHours } from '../hooks/useAppSettings';
import { getHolidays } from '../hooks/usePublicHolidays';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Resolves the time-window classification for the given date/time.
 *
 * Source of business hours, in order:
 *  1. ``config`` parameter (explicit override — wins everything)
 *  2. AppSettings cache via ``getBusinessHours()`` (admin-driven)
 *  3. ``BUSINESS_HOURS_DEFAULT`` (compile-time fallback, used when the
 *     settings cache is cold OR the saved value is null)
 *
 * Check order:
 *  1. Public holiday → after-hours
 *  2. Weekend → after-hours
 *  3. Hour outside shoulder range → after-hours
 *  4. Hour inside shoulder but outside business → shoulder
 *  5. Otherwise → business
 */
export function resolveTimeWindow(
  date: Date,
  holidays: PublicHoliday[],
  config?: Partial<BusinessHoursConfig>,
  stateCode?: string
): TimeWindowResult {
  const base = config ? BUSINESS_HOURS_DEFAULT : getBusinessHours();
  const merged: BusinessHoursConfig = { ...base, ...config };

  // Get local date components in the target timezone
  const localDateStr = date.toLocaleDateString('en-CA', { timeZone: merged.timezone }); // YYYY-MM-DD
  const localHour = parseInt(
    date.toLocaleString('en-US', { timeZone: merged.timezone, hour: 'numeric', hour12: false }),
    10
  );
  const localDayOfWeek = new Date(
    date.toLocaleString('en-US', { timeZone: merged.timezone })
  ).getDay();

  // 1. Holiday check
  const matchingHoliday = holidays.find(h => {
    if (h.date !== localDateStr) return false;
    if (h.global) return true;
    if (stateCode && h.counties) return h.counties.includes(stateCode);
    return true; // conservative: treat all holidays as applicable if no stateCode
  });

  if (matchingHoliday) {
    return { window: 'after-hours', isAfterHours: true, reason: `Public holiday: ${matchingHoliday.name}` };
  }

  // 2. Weekend check
  if (!merged.workDays.includes(localDayOfWeek)) {
    return { window: 'after-hours', isAfterHours: true, reason: `Weekend (${DAY_NAMES[localDayOfWeek]})` };
  }

  // 3. Outside shoulder range entirely → after-hours
  if (localHour < merged.shoulderStartHour || localHour >= merged.shoulderEndHour) {
    const start = String(merged.shoulderStartHour).padStart(2, '0');
    const end = String(merged.shoulderEndHour).padStart(2, '0');
    return {
      window: 'after-hours',
      isAfterHours: true,
      reason: `Outside business hours (${start}:00\u2013${end}:00)`,
    };
  }

  // 4. Inside shoulder but outside business → shoulder
  if (localHour < merged.businessStartHour || localHour >= merged.businessEndHour) {
    const bStart = String(merged.businessStartHour).padStart(2, '0');
    const bEnd = String(merged.businessEndHour).padStart(2, '0');
    return {
      window: 'shoulder',
      isAfterHours: true,
      reason: `Shoulder hours (outside ${bStart}:00\u2013${bEnd}:00)`,
    };
  }

  // 5. Business hours
  return { window: 'business', isAfterHours: false };
}

/**
 * Return an instant that classifies as the standard `business` window — a
 * workday, non-holiday, within business hours.
 *
 * Used to price a recurring ticket's date/time-sensitive line items (e.g.
 * support labour) at the standard rate. A recurring definition is a frozen
 * snapshot replayed on every run, so pricing it against the wall-clock at save
 * time would bake in whatever window it happened to be saved in (evening,
 * weekend, holiday → after-hours SKU + rate). Pinning to a business-hours
 * reference makes recurring tickets always resolve to the standard rate,
 * regardless of when they are saved or when the timer fires.
 *
 * Walks forward from `from` in hourly steps, delegating to `resolveTimeWindow`
 * so the admin-configured business hours + public holidays are honoured. The
 * search is bounded (~2 weeks) so a misconfiguration can't loop forever; the
 * last candidate is returned as a best effort in that case.
 */
export function standardBusinessHoursReference(
  from: Date,
  holidays: PublicHoliday[] = getHolidays(),
): Date {
  const candidate = new Date(from.getTime());
  candidate.setHours(9, 0, 0, 0);
  const HOUR_MS = 60 * 60 * 1000;
  for (let i = 0; i < 24 * 14; i++) {
    if (resolveTimeWindow(candidate, holidays).window === 'business') {
      return candidate;
    }
    candidate.setTime(candidate.getTime() + HOUR_MS);
  }
  return candidate;
}
